/**
 * Строка списка дел.
 *
 * `variant="my"` — семь колонок блока «Мои дела» из макета (`.my-row`, ui-blocks §7.4).
 * `variant="full"` — девять колонок списка дел (ТЗ §8.4) на строке `.list-row.cases-row`.
 *
 * Типы DTO — `@/api/types`, форматирование — `@/lib/*`; здесь только разметка и то,
 * что нужно именно строке. Инлайновый стиль один и он из макета: ширина `.prog-mini > i`.
 */
import type { KeyboardEvent, ReactNode } from 'react';
import type { CaseListItem } from '@/api/types';
import { deadlineClass, leftText } from '@/lib/deadlines';
import { fmtShort } from '@/lib/format';
import { categoryLabel } from '@/lib/categories';
import { isOpenStage, stageChipClass, stageLabel } from '@/lib/stages';

export type CaseRowVariant = 'my' | 'full';

/** Бейдж приоритета: в макете он есть только у 1 и 2, у 3–5 колонка пустая. */
export function priorityBadge(priority: number | null | undefined): ReactNode {
    if (priority === 1) return <span className="badge p1">П1</span>;
    if (priority === 2) return <span className="badge p2">П2</span>;
    return null;
}

/** Значки темы: риск проверки и повторное обращение. */
export function caseFlags(item: CaseListItem): ReactNode {
    return (
        <>
            {item.escalation_risk ? (
                <span className="flag" title="риск проверки">
                    🛡
                </span>
            ) : null}
            {item.repeat_of ? (
                <span className="flag" title="повторное обращение">
                    🔁
                </span>
            ) : null}
        </>
    );
}

/** Дело считается открытым, пока не отправлен ответ. */
export function isOpenCase(item: CaseListItem): boolean {
    return isOpenStage(item.stage) && !item.closed_at;
}

/** Розовая подсветка строки — только у открытого просроченного дела. */
export function isLateRow(item: CaseListItem): boolean {
    return isOpenCase(item) && (item.days_left ?? 0) < 0;
}

export interface DeadlineCell {
    text: string;
    /** Вторая строка ячейки: «продлено до ДД.ММ». */
    extra: string | null;
    className: string;
}

/**
 * Ячейка срока (макет, `#myBlock`): у закрытого дела — дата закрытия без цвета,
 * у открытого — текст срока от бэкенда, у продлённого добавляется вторая строка.
 */
export function deadlineCell(item: CaseListItem): DeadlineCell {
    if (!isOpenCase(item)) {
        return {
            text: `закрыто ${fmtShort(item.closed_at ?? item.sent_at)}`,
            extra: null,
            className: 'my-date',
        };
    }
    const modifier = deadlineClass(item.days_left);
    const text = item.deadline_text || leftText(item.legal_deadline, item.days_left);
    if (item.extended) {
        return {
            text,
            extra: `продлено до ${fmtShort(item.legal_deadline)}`,
            className: `my-date ${modifier || 'ext'}`,
        };
    }
    return { text, extra: null, className: `my-date ${modifier}`.trim() };
}

/** Enter и пробел на строке-кнопке работают как клик (макет, обработка клавиатуры). */
export function rowKeyDown(event: KeyboardEvent<HTMLElement>, run: () => void): void {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        run();
    }
}

export interface CaseRowProps {
    item: CaseListItem;
    variant: CaseRowVariant;
    onOpen: (caseId: string) => void;
}

export function CaseRow({ item, variant, onOpen }: CaseRowProps) {
    const open = () => onOpen(item.case_id);
    const deadline = deadlineCell(item);
    const late = isLateRow(item);
    const done = item.checklist_done ?? 0;
    const total = item.checklist_total ?? 0;
    const percent = total > 0 ? Math.round((done / total) * 100) : 0;

    const dateCell = (
        <span className={deadline.className}>
            {deadline.text}
            {deadline.extra ? (
                <>
                    <br />
                    {deadline.extra}
                </>
            ) : null}
        </span>
    );

    if (variant === 'my') {
        return (
            <div
                className={late ? 'my-row late-row' : 'my-row'}
                data-case={item.case_id}
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(event) => rowKeyDown(event, open)}
            >
                <span className="my-id">{item.number}</span>
                <span>{priorityBadge(item.priority)}</span>
                <span className="my-title">
                    {item.title}
                    {caseFlags(item)}
                </span>
                <span className="my-appl">{item.applicant ?? '—'}</span>
                {dateCell}
                <span>
                    <span className={stageChipClass(item.stage)}>{stageLabel(item.stage)}</span>
                </span>
                <span className="prog-wrap">
                    <span className="prog-mini">
                        <i style={{ width: `${percent}%` }} />
                    </span>
                    <span className="prog-txt">
                        {done} из {total}
                    </span>
                </span>
            </div>
        );
    }

    return (
        <div
            className={late ? 'list-row cases-row late-row' : 'list-row cases-row'}
            data-case={item.case_id}
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(event) => rowKeyDown(event, open)}
        >
            <span className="my-id">
                {item.number}
                <span className="cell-sub">поступило {fmtShort(item.received_at || item.created_at)}</span>
            </span>
            <span className="my-title">
                {item.title}
                {caseFlags(item)}
            </span>
            <span className="my-appl">{categoryLabel(item.primary_category) || '—'}</span>
            <span>{priorityBadge(item.priority)}</span>
            <span>
                <span className={stageChipClass(item.stage)}>{stageLabel(item.stage)}</span>
            </span>
            <span className="lr-exec" title={item.executor_name ?? 'без исполнителя'}>
                {item.executor_name ?? 'без исполнителя'}
            </span>
            {dateCell}
            <span className="prog-txt">{fmtShort(item.last_event_at ?? item.updated_at)}</span>
        </div>
    );
}

export default CaseRow;
